import { REGION_COORDINATES } from '@/mocks/regionData';

export interface LatLng {
  lat: number;
  lng: number;
}

export function haversineDistance(
  lat1: number,
  lon1: number,
  lat2: number,
  lon2: number,
): number {
  const R = 6371;
  const dLat = toRad(lat2 - lat1);
  const dLon = toRad(lon2 - lon1);
  const a =
    Math.sin(dLat / 2) * Math.sin(dLat / 2) +
    Math.cos(toRad(lat1)) * Math.cos(toRad(lat2)) *
    Math.sin(dLon / 2) * Math.sin(dLon / 2);
  const c = 2 * Math.atan2(Math.sqrt(a), Math.sqrt(1 - a));
  return R * c;
}

function toRad(deg: number): number {
  return (deg * Math.PI) / 180;
}

export function formatDistance(km: number): string {
  if (!isFinite(km)) return '거리 미상';
  if (km < 1) {
    return `${Math.round(km * 1000)}m`;
  }
  if (km < 10) {
    return `${km.toFixed(1)}km`;
  }
  return `${Math.round(km)}km`;
}

// 지역명("서울 강남구", "강남구", "서울") → 좌표 조회
export function getRegionCoordinate(regionKey: string): LatLng | null {
  const key = regionKey?.trim();
  if (!key) return null;
  const direct = REGION_COORDINATES[key];
  if (direct) return direct;
  const province = key.split(' ')[0];
  return REGION_COORDINATES[province] || null;
}

// 입력 텍스트에 맞는 지역 좌표 자동완성 (정확 일치 → 접두사 → 길이순)
export function searchRegionCoordinates(
  text: string,
): { key: string; lat: number; lng: number }[] {
  const q = text.trim();
  if (!q) return [];
  const results: { key: string; lat: number; lng: number }[] = [];
  Object.entries(REGION_COORDINATES).forEach(([key, coord]) => {
    if (key.includes(q)) {
      results.push({ key, lat: coord.lat, lng: coord.lng });
    }
  });
  results.sort((a, b) => {
    const aExact = a.key === q ? 0 : 1;
    const bExact = b.key === q ? 0 : 1;
    if (aExact !== bExact) return aExact - bExact;
    const aPrefix = a.key.startsWith(q) ? 0 : 1;
    const bPrefix = b.key.startsWith(q) ? 0 : 1;
    if (aPrefix !== bPrefix) return aPrefix - bPrefix;
    return a.key.length - b.key.length;
  });
  return results.slice(0, 8);
}

// 간병인과 내 위치 사이의 거리 = (프로필 좌표 or 활동 지역들) 중 가장 가까운 곳
export function getCaregiverDistance(
  myLat: number,
  myLng: number,
  profileLat: number,
  profileLng: number,
  regions: string[],
): number {
  const candidates: LatLng[] = [];
  if (profileLat && profileLng) {
    candidates.push({ lat: profileLat, lng: profileLng });
  }
  (regions || []).forEach((region) => {
    const coord = getRegionCoordinate(region);
    if (coord) candidates.push(coord);
  });
  if (candidates.length === 0) return Infinity;
  return Math.min(...candidates.map((c) => haversineDistance(myLat, myLng, c.lat, c.lng)));
}

// 한국 행정구역 풀네임 → 축약 시/도명 정규화 (풀주소 "서울특별시 강남구 ..." 대응)
const PROVINCE_ALIASES: Record<string, string> = {
  서울특별시: '서울',
  서울시: '서울',
  부산광역시: '부산',
  인천광역시: '인천',
  대구광역시: '대구',
  광주광역시: '광주',
  대전광역시: '대전',
  울산광역시: '울산',
  세종특별자치시: '세종',
  세종시: '세종',
  경기도: '경기',
  강원특별자치도: '강원',
  강원도: '강원',
  충청북도: '충북',
  충청남도: '충남',
  전라북도: '전북',
  전북특별자치도: '전북',
  전라남도: '전남',
  경상북도: '경북',
  경상남도: '경남',
  제주특별자치도: '제주',
};

// 주소/지역 텍스트에서 가장 구체적인 지역 좌표를 찾는다 (풀주소 포함)
function getCoordinateFromText(text: string): LatLng | null {
  const raw = text?.trim();
  if (!raw) return null;

  const direct = REGION_COORDINATES[raw];
  if (direct) return direct;

  let normalized = raw;
  Object.entries(PROVINCE_ALIASES).forEach(([full, short]) => {
    normalized = normalized.replace(full, short);
  });

  if (REGION_COORDINATES[normalized]) return REGION_COORDINATES[normalized];

  let best: LatLng | null = null;
  let bestLen = 0;
  Object.entries(REGION_COORDINATES).forEach(([regionKey, coord]) => {
    if (regionKey.includes(' ') && normalized.includes(regionKey) && regionKey.length > bestLen) {
      best = coord;
      bestLen = regionKey.length;
    }
  });
  if (best) return best;

  const province = normalized.split(' ')[0];
  return REGION_COORDINATES[province] || null;
}

// 환자와 내 위치 사이의 거리 = 간병장소(병원/집) 위치 기준
export function getPatientDistance(
  myLat: number,
  myLng: number,
  profileLat: number,
  profileLng: number,
  careRegion: string,
  careAddress: string,
): number {
  if (profileLat && profileLng) {
    return haversineDistance(myLat, myLng, profileLat, profileLng);
  }
  const coord = getCoordinateFromText(careAddress) || getCoordinateFromText(careRegion);
  if (coord) return haversineDistance(myLat, myLng, coord.lat, coord.lng);
  return Infinity;
}

export const DEFAULT_USER_LOCATION = {
  lat: 37.5665,
  lng: 126.978,
  label: '서울시청',
};